import { StateCreator } from 'zustand';
import { ConsumerToggle, ConsumerToggleResponse } from '../types/ConsumerToggle';
import { ConsumerLocale } from '../types/ConsumerLocale';
import { immer } from 'zustand/middleware/immer';
import { persist } from 'zustand/middleware';
import { ApiResponse } from '../types/ApiResponse';
import { detectLanguage, getConsumerToggleOptions, getSupportedLanguages, translateText } from '../api/translate';
import { unescapeHtmlEntities } from '../util/StringUtil';

export type AppState = {
	rnResponseProjectId: number | null | undefined;
	isLoading: boolean;
	selectedConsumerId: string | null;
	selectedConsumerName: string | null;
	selectedSourceLocale: number | null;
	selectedTargetLocale: number | null;
	consumerToggleOptions: ConsumerToggle[];
	consumerLocales: ConsumerLocale[];
	detectedLanguage: string | null;
	textBefore: string | undefined;
	textAfter: string | undefined;
	initialTextToTranslate: string | null;
	autoDetectLanguageEnabled: boolean;
	autoTranslate: boolean;
	isFromAutoDetect: boolean;
};

export type AppActions = {
	setRnResponseProjectId: (rnResponseProjectId: number | null | undefined) => void;
	updateSelectedConsumer: (consumer: ConsumerToggle) => void;
	setSelectedSourceLocale: (localeId: number) => void;
	setSelectedTargetLocale: (localeId: number) => void;
	setTextBefore: (text: string) => void;
	setTextAfter: (text: string) => void;
	setInitialTextToTranslate: (text: string) => void;
	setAutoDetectLanguageEnabled: (enabled: boolean) => void;
	setAutoTranslate: (enabled: boolean) => void;
	setIsFromAutoDetect: (enabled: boolean) => void;
	fetchConsumerToggleOptions: (token: string) => Promise<ApiResponse>;
	setLoading: (isLoading: boolean) => void;
	fetchLanguagesByConsumer: (token: string, consumerId: string) => Promise<ApiResponse>;
	autoDetectLang: (text: string, auth0IdToken: string | null, consumerId: string | null) => Promise<ApiResponse>;
	translateText: (text: string, sourceLocaleId: number, targetLocaleId: number, auth0IdToken: string | null, consumerId: string | null) => Promise<ApiResponse>;
	reset: () => void;
};

export type AppSlice = AppState & AppActions;

export const createAppSlice: StateCreator<
	AppSlice,
	[],
	[['zustand/persist', unknown], ['zustand/immer', unknown]]
> = persist(
	immer((set, get) => ({
		rnResponseProjectId: null,
		selectedConsumerId: null,
		selectedConsumerName: null,
		selectedSourceLocale: null,
		selectedTargetLocale: null,
		consumerToggleOptions: [],
		consumerLocales: [],
		detectedLanguage: null,
		textBefore: undefined,
		textAfter: undefined,
		initialTextToTranslate: null,
		isTextBeforeRtl: false,
		isTextAfterRtl: false,
		autoDetectLanguageEnabled: true,
		autoTranslate: false,
		isFromAutoDetect: false,
		isLoading: false,

		setRnResponseProjectId: (rnResponseProjectId: number | null | undefined) => {
			set((state) => {
				state.rnResponseProjectId = rnResponseProjectId;
			});
		},
		updateSelectedConsumer: (consumerToggle: ConsumerToggle) => {
			set((state) => {
				state.selectedConsumerId = consumerToggle.id;
				state.selectedConsumerName = consumerToggle.name;
			});

			// reset consumer locales when switching selecting consumer
			set((state) => {
				state.consumerLocales = [];
			});
		},

		setSelectedSourceLocale: (localeId: number) => {
			set((state) => {
				state.selectedSourceLocale = localeId;
			});
		},

		setSelectedTargetLocale: (localeId: number) => {
			set((state) => {
				state.selectedTargetLocale = localeId;
			});
		},

		setTextBefore: (text: string | undefined) => {
			set((state) => {
				state.textBefore = text;
			});
		},

		setTextAfter: (text: string | undefined)  => {
			set((state) => {
				state.textAfter = text;
			});
		},

		setAutoDetectLanguageEnabled: (autoDetectLanguageEnabled: boolean) => {
			set((state) => {
				state.autoDetectLanguageEnabled = autoDetectLanguageEnabled;
			});
		},

		setInitialTextToTranslate: (text: string | null) => {
			set((state) => {
				state.initialTextToTranslate = text;
			});
		},

		setAutoTranslate: (autoTranslate: boolean) => {
			set((state) => {
				state.autoTranslate = autoTranslate;
			});
		},

		setIsFromAutoDetect: (isFromAutoDetect: boolean) => {
			set((state) => {
				state.isFromAutoDetect = isFromAutoDetect;
			});
		},
		fetchConsumerToggleOptions: async (token: string): Promise<ApiResponse> => {
			// Check if token exists and is not empty
			if (!token) {
				return {
					data: null,
					hasError: true,
					error: { name: 'auth-error', message: 'Authentication token expired. Please log in again.'},
				};
			}

			if (get().consumerToggleOptions.length === 0) {
				set((state) => {
					state.isLoading = true;
				});
			}

			const response: ApiResponse | null = await getConsumerToggleOptions(token);

			if (response.hasError) {
				console.error('Failed to fetch consumer toggles:', response.error);
			} else if (response.data) {
				const responseData: ConsumerToggleResponse = response.data;

				set((state) => {
					state.consumerToggleOptions = responseData.consumerToggleOptions;
				});

				if (get().selectedConsumerName === null) {
					set((state) => {
						state.selectedConsumerId = responseData.selectedConsumerId;
						state.selectedConsumerName = responseData.selectedConsumer;
					});
				}

				const { selectedConsumerId, selectedConsumerName } = get();
				console.debug(
					`Default Selected consumer: ${selectedConsumerName} (${selectedConsumerId})`
				);
			}

			set((state) => {
				state.isLoading = false;
			});

			return response;
		},
		setLoading: (isLoading: boolean) => {
			set((state) => {
				state.isLoading = isLoading;
			});
		},
		fetchLanguagesByConsumer: async (
			token: string,
			consumerId: string
		): Promise<ApiResponse> => {
			// Check if token exists and is not empty
			if (!token) {
				return {
					data: null,
					hasError: true,
					error: { name: 'auth-error', message: 'Authentication token expired. Please log in again.'},
				};
			}

			if (!consumerId) {
				return {
					data: null,
					hasError: true,
					error: {
						name: 'consumer-id-error',
						message: 'Consumer ID is required to fetch languages.'
					},
				};
			}

			set((state) => {
				state.isLoading = true;
			});

			if (get().consumerLocales.length > 0) {
				set((state) => {
					state.isLoading = false;
				});
			}

			const response = await getSupportedLanguages(token, consumerId);

			if (response.hasError) {
				console.error('Failed to fetch languages:', response.error);
			} else if (response.data) {
				// Update store with fetched data
				set((state) => {
					state.consumerLocales = response.data!;
				});
			}

			// Reset loading state
			set((state) => {
				state.isLoading = false;
			});

			return response;
		},
		autoDetectLang: async (text: string, auth0IdToken: string | null, consumerId: string | null) : Promise<ApiResponse> => {
			set((state) => {
				state.isLoading = true;
			});

			const response = await detectLanguage(text, auth0IdToken, consumerId);

			if (response.hasError) {
				console.error('Failed to detect language:', response.error);
			} else if (response.data) {
				set((state) => {
					state.detectedLanguage = response.data
				});
			}

			if (get().consumerLocales.length === 0) {
				await get().fetchLanguagesByConsumer(auth0IdToken!, consumerId!)
			}

			const detectedLocale = response.data;
			if (detectedLocale) {
				let detectedConsumerLocale = get().consumerLocales.find(i => i.consumerLocaleId === detectedLocale);
				if (detectedConsumerLocale) {
					set((state) => {
						state.selectedSourceLocale = detectedConsumerLocale?.lioLocaleId!
					});
				}
			}
			set((state) => {
				state.isLoading = false;
			});
			return response;
		},
		translateText: async (text: string, sourceLocaleId: number, targetLocaleId: number, auth0IdToken: string | null, consumerId: string | null) => {
			const fromLocale = get().consumerLocales.find(i => i.lioLocaleId === sourceLocaleId);
			const toLocale = get().consumerLocales.find(i => i.lioLocaleId === targetLocaleId);
			const response = await translateText(text, fromLocale?.consumerLocaleId, toLocale?.consumerLocaleId, get().rnResponseProjectId || null, auth0IdToken, consumerId);
			if(response.hasError) {
				console.error('Failed to translate text:', response.error);
			} else if (response.data) {
				set((state) => {
					state.textAfter = unescapeHtmlEntities(response.data);
					state.rnResponseProjectId = response.data?.rnResponseProjectId || null;
				});
			}
			console.debug(
				`Translated text from ${fromLocale?.name} to ${toLocale?.name}:`,
			)
			return response;
		},
		/**
		 * Reset store to initial state
		 * Clears consumer data
		 */
		reset: () => {
			set((state) => {
				// Reset loading state
				state.isLoading = false;

				state.selectedConsumerId = null;
				state.selectedConsumerName = null;
				state.consumerToggleOptions = [];
				state.consumerLocales = [];
			});
		}
	})),
	{
		name: 'lio-chrome-sidebar-app',
		partialize: (state) => ({
			selectedConsumerId: state.selectedConsumerId,
			selectedConsumerName: state.selectedConsumerName,
			selectedSourceLocale: state.selectedSourceLocale,
			selectedTargetLocale: state.selectedTargetLocale,
			consumerToggleOptions: state.consumerToggleOptions,
			consumerLocales: state.consumerLocales,
			autoDetectLanguageEnabled: state.autoDetectLanguageEnabled,
			autoTranslate: state.autoTranslate
		})
	}
);
